import { format, parseISO } from 'date-fns';
import {
  PencilSimple, Trash, WhatsappLogo, Phone, FileText, FilePdf, CheckCircle, Eye,
} from '@phosphor-icons/react';
import { EditableCell } from './EditableCell';
import {
  buildWhatsAppLink, buildTelLink, fmtInr,
  STATUS_OPTIONS, getStatusBadgeClass,
} from './helpers';

/**
 * Single candidate row with inline-editable cells, charge/balance columns (owner only)
 * and the action button group (WhatsApp / Call / Agreement / Edit / Mark joined / Delete).
 */
export const CandidateRow = ({
  candidate,
  month,
  recruiters,
  companySuggestions = null,
  isOwner,
  canSeeFinance = isOwner,
  needsFollowup,
  inlineSave,
  onSendAgreement,
  onPreviewAgreementPdf,
  onDownloadAgreementPdf,
  onOpenEdit,
  onMarkJoined,
  onDelete,
}) => {
  const charge = Number(candidate.consultancy_charge) || 0;
  const effective = candidate.charge_override != null ? Number(candidate.charge_override) : charge;
  const paid = (candidate.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const balance = effective - paid;
  const isJoined = (candidate.joining_status || '').toLowerCase() === 'joined';
  const monthOf = (d) => (d ? d.slice(0, 7) : '');
  const monthLabel = (d) => format(parseISO(d), 'MMM');
  const joinsOtherMonth = !!month && monthOf(candidate.date_of_joining) && monthOf(candidate.date_of_joining) !== month;
  const linedUpOtherMonth = !!month && monthOf(candidate.interview_date) && monthOf(candidate.interview_date) !== month;
  const recruiterOptions = recruiters.length > 0
    ? ['', ...recruiters.map(r => r.name)]
    : null;

  return (
    <tr
      className={needsFollowup ? 'followup-highlight' : ''}
      data-testid={needsFollowup ? 'followup-row' : 'candidate-row'}
    >
      <td className="font-bold">
        <EditableCell
          testId={`inline-name-${candidate.id}`}
          value={candidate.candidate_name}
          onSave={(v) => inlineSave(candidate, 'candidate_name', v)}
        />
      </td>
      <td>
        <EditableCell
          testId={`inline-number-${candidate.id}`}
          value={candidate.candidate_number}
          onSave={(v) => inlineSave(candidate, 'candidate_number', v)}
        />
      </td>
      <td>
        <EditableCell
          testId={`inline-company-${candidate.id}`}
          value={candidate.company_name}
          suggestions={companySuggestions}
          onSave={(v) => inlineSave(candidate, 'company_name', v)}
        />
      </td>
      <td>
        <EditableCell
          testId={`inline-category-${candidate.id}`}
          value={candidate.job_category || ''}
          onSave={(v) => inlineSave(candidate, 'job_category', v)}
        />
      </td>
      <td>
        <EditableCell
          testId={`inline-interview-${candidate.id}`}
          type="date"
          value={candidate.interview_date}
          displayValue={
            <>
              {format(parseISO(candidate.interview_date), 'dd MMM yyyy')}
              {linedUpOtherMonth && (
                <span data-testid={`lined-up-badge-${candidate.id}`} className="ml-1 px-1.5 py-0.5 bg-[#93C5FD] text-black rounded text-[10px] font-bold align-middle whitespace-nowrap">
                  LINED UP {monthLabel(candidate.interview_date).toUpperCase()}
                </span>
              )}
            </>
          }
          onSave={(v) => inlineSave(candidate, 'interview_date', v)}
        />
      </td>
      <td>
        <EditableCell
          testId={`inline-joining-${candidate.id}`}
          type="date"
          value={candidate.date_of_joining}
          displayValue={
            <>
              {format(parseISO(candidate.date_of_joining), 'dd MMM yyyy')}
              {joinsOtherMonth && (
                <span data-testid={`joins-badge-${candidate.id}`} className="ml-1 px-1.5 py-0.5 bg-[#FDE047] text-black rounded text-[10px] font-bold align-middle whitespace-nowrap">
                  {isJoined ? 'JOINED' : 'JOINS'} {monthLabel(candidate.date_of_joining).toUpperCase()}
                </span>
              )}
            </>
          }
          onSave={(v) => inlineSave(candidate, 'date_of_joining', v)}
        />
      </td>
      <td>
        <EditableCell
          testId={`inline-recruiter-${candidate.id}`}
          value={candidate.recruiter_name || ''}
          options={recruiterOptions}
          onSave={(v) => inlineSave(candidate, 'recruiter_name', v)}
        />
      </td>
      <td>
        <EditableCell
          testId={`inline-status-${candidate.id}`}
          value={candidate.joining_status}
          options={STATUS_OPTIONS}
          displayValue={
            <>
              <span className={getStatusBadgeClass(candidate.joining_status)}>
                {candidate.joining_status}
              </span>
              {needsFollowup && (
                <span className="ml-2 px-2 py-1 bg-[#F87171] text-white rounded text-xs font-bold">ACTION</span>
              )}
            </>
          }
          onSave={(v) => inlineSave(candidate, 'joining_status', v)}
        />
      </td>
      {canSeeFinance && (
        <td className="font-bold" data-testid={`charge-cell-${candidate.id}`}>
          {isJoined ? (
            <>
              {fmtInr(effective)}
              {candidate.charge_override != null && (
                <span
                  className="ml-1 px-1.5 py-0.5 bg-[#F59E0B] text-black rounded text-[10px] font-bold align-middle"
                  title={`Adjusted from ₹${charge}${candidate.override_reason ? ' — ' + candidate.override_reason : ''}`}
                >
                  ADJ
                </span>
              )}
            </>
          ) : (
            <span className="text-white/50" title={`₹${effective} — counted only once the candidate joins`}>—</span>
          )}
        </td>
      )}
      {canSeeFinance && (
        <td className={balance > 0.01 ? 'font-bold text-[#FDE047]' : 'font-bold text-[#A3E635]'} data-testid={`balance-cell-${candidate.id}`}>
          {!isJoined ? (
            <span className="px-2 py-0.5 bg-white/15 text-white/80 border border-white/30 rounded text-[10px] font-bold whitespace-nowrap">NOT JOINED</span>
          ) : balance <= 0.01 ? 'CLEARED' : fmtInr(balance)}
        </td>
      )}
      <td>
        <div className="flex gap-2">
          {buildWhatsAppLink(candidate.candidate_number) && (
            <a
              data-testid={`row-whatsapp-${candidate.id}`}
              href={buildWhatsAppLink(candidate.candidate_number)}
              target="_blank"
              rel="noopener noreferrer"
              title="Send WhatsApp"
              className="p-2 bg-[#25D366] text-black border-2 border-[#25D366] rounded hover:shadow-[2px_2px_0px_0px_rgba(37,211,102,0.5)] hover:-translate-y-0.5 transition-all"
            >
              <WhatsappLogo size={16} weight="fill" />
            </a>
          )}
          {buildTelLink(candidate.candidate_number) && (
            <a
              data-testid={`row-call-${candidate.id}`}
              href={buildTelLink(candidate.candidate_number)}
              title="Call"
              className="p-2 bg-white text-black border-2 border-white rounded hover:shadow-[2px_2px_0px_0px_rgba(255,255,255,0.4)] hover:-translate-y-0.5 transition-all"
            >
              <Phone size={16} weight="fill" />
            </a>
          )}
          {isOwner && (
            <button
              data-testid={`send-agreement-${candidate.id}`}
              onClick={() => onSendAgreement(candidate)}
              title="Send Employment Agreement text on WhatsApp"
              className="p-2 bg-[#A3E635] text-black border-2 border-black rounded hover:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] hover:-translate-y-0.5 transition-all relative"
            >
              <FileText size={16} weight="fill" />
              {candidate.agreement_sent_at && (
                <CheckCircle
                  size={12}
                  weight="fill"
                  className="absolute -top-1 -right-1 text-[#25D366] bg-white rounded-full"
                  data-testid={`sent-badge-${candidate.id}`}
                />
              )}
            </button>
          )}
          {isOwner && (
            <button
              data-testid={`preview-pdf-${candidate.id}`}
              onClick={() => onPreviewAgreementPdf(candidate)}
              title="Preview Agreement PDF in a new tab"
              className="p-2 bg-[#FDE047] text-black border-2 border-black rounded hover:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] hover:-translate-y-0.5 transition-all"
            >
              <Eye size={16} weight="fill" />
            </button>
          )}
          {isOwner && (
            <button
              data-testid={`download-pdf-${candidate.id}`}
              onClick={() => onDownloadAgreementPdf(candidate)}
              title="Download Agreement as PDF (attach manually in WhatsApp)"
              className="p-2 bg-[#F87171] text-white border-2 border-black rounded hover:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] hover:-translate-y-0.5 transition-all"
            >
              <FilePdf size={16} weight="fill" />
            </button>
          )}
          <button
            data-testid={`edit-candidate-${candidate.id}`}
            onClick={() => onOpenEdit(candidate)}
            className="p-2 bg-[#93C5FD] border-2 border-[#0A0A0A] rounded hover:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] transition-all"
          >
            <PencilSimple size={16} weight="bold" />
          </button>
          {candidate.joining_status !== 'Joined' && (
            <button
              data-testid={`mark-joined-${candidate.id}`}
              onClick={() => onMarkJoined(candidate)}
              title="Mark as Joined"
              className="p-2 bg-[#4ADE80] text-black border-2 border-[#0A0A0A] rounded hover:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] hover:-translate-y-0.5 transition-all"
            >
              <CheckCircle size={16} weight="fill" />
            </button>
          )}
          {isOwner && (
            <button
              data-testid={`delete-candidate-${candidate.id}`}
              onClick={() => onDelete(candidate.id)}
              className="p-2 bg-[#F87171] text-white border-2 border-[#0A0A0A] rounded hover:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] transition-all"
            >
              <Trash size={16} weight="bold" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};
